import type {
    AttributeErrorTO,
    DeviationTO,
    LiveControlWriteRequestTO,
    MrmStateTO,
    PositionTO,
    VehicleRepresentationTO,
} from '@control-center/control-center-client-public-domain-simulation-ts';
import {
    VehicleAttributeKeyTO,
    type VehicleAttributeKeyTO as VehicleAttributeKeyTOType,
} from '@control-center/control-center-client-public-domain-simulation-ts';
import Button from 'components/actions/Button';
import NoData from 'components/content/NoData';
import type React from 'react';

import {
    ADS_STATE_OPTIONS,
    BooleanToggleControl,
    DRIVING_GEAR_OPTIONS,
    EnumSelectControl,
    ErrorModeSelect,
    LatencyControl,
    MISSION_STATE_OPTIONS,
    MrmStateControl,
    PositionControl,
    SpeedControl,
} from './AttributeEditControls';
import DeviationEditor from './DeviationEditor';
import EditableAttribute, { ErrorToggle, VehicleAttributeRow } from './EditableAttribute';
import { type ResolvedAttribute, useAttributeEditState } from './hooks/useAttributeEditState';
import MrmStateDisplay, { AttributeErrorBadge } from './MrmStateDisplay';

interface AttributeSectionProps {
    title: string;
    children: React.ReactNode;
}

const AttributeSection = ({ title, children }: AttributeSectionProps) => (
    <div className='SimulationVehicleAttributes__group'>
        <p className='SimulationVehicleAttributes__groupTitle'>{title}</p>
        {children}
    </div>
);

function renderAttributeValue<T>(
    field: { value?: T; error?: AttributeErrorTO } | undefined,
    render: (value: T) => React.ReactNode
): React.ReactNode {
    if (field?.error) return <AttributeErrorBadge error={field.error} />;
    if (field?.value !== undefined) return <>{render(field.value)}</>;
    return <NoData className='text-14' />;
}

// ── attribute registry ───────────────────────────────────────────────────────

interface AttributeConfig<T = unknown> {
    key: VehicleAttributeKeyTOType;
    field: keyof LiveControlWriteRequestTO;
    label: string;
    testId: string;
    renderValue: (serverField: { value?: T; error?: AttributeErrorTO } | undefined) => React.ReactNode;
    renderControl: (resolved: ResolvedAttribute) => React.ReactNode;
}

function defineAttr<T>(config: AttributeConfig<T>): AttributeConfig<T> {
    return config;
}

const ATTRIBUTES = {
    position: defineAttr<PositionTO>({
        key: VehicleAttributeKeyTO.Position,
        field: 'position',
        label: 'Position',
        testId: 'vehicle-attr-position',
        renderValue: field =>
            renderAttributeValue(field, pos => `${pos.latitude.toFixed(4)}, ${pos.longitude.toFixed(4)}`),
        renderControl: resolved => (
            <PositionControl
                entry={resolved.display as Parameters<typeof PositionControl>[0]['entry']}
                onValueChange={resolved.onValueChange}
            />
        ),
    }),
    speedMs: defineAttr<number>({
        key: VehicleAttributeKeyTO.SpeedMs,
        field: 'speedMs',
        label: 'Speed',
        testId: 'vehicle-attr-speed',
        renderValue: field => renderAttributeValue(field, speed => `${speed.toFixed(2)} m/s`),
        renderControl: resolved => (
            <SpeedControl
                entry={resolved.display as Parameters<typeof SpeedControl>[0]['entry']}
                onValueChange={resolved.onValueChange}
            />
        ),
    }),
    drivingGear: defineAttr<string>({
        key: VehicleAttributeKeyTO.DrivingGear,
        field: 'drivingGear',
        label: 'Driving Gear',
        testId: 'vehicle-attr-drivingGear',
        renderValue: field => renderAttributeValue(field, value => value),
        renderControl: resolved => (
            <EnumSelectControl
                entry={resolved.display as Parameters<typeof EnumSelectControl>[0]['entry']}
                options={DRIVING_GEAR_OPTIONS}
                onValueChange={resolved.onValueChange}
            />
        ),
    }),
    parkingBrake: defineAttr<boolean>({
        key: VehicleAttributeKeyTO.ParkingBrake,
        field: 'parkingBrake',
        label: 'Parking Brake',
        testId: 'vehicle-attr-parkingBrake',
        renderValue: field => renderAttributeValue(field, value => String(value)),
        renderControl: resolved => (
            <BooleanToggleControl
                entry={resolved.display as Parameters<typeof BooleanToggleControl>[0]['entry']}
                onValueChange={resolved.onValueChange}
            />
        ),
    }),
    adsState: defineAttr<string>({
        key: VehicleAttributeKeyTO.AdsState,
        field: 'adsState',
        label: 'ADS State',
        testId: 'vehicle-attr-adsState',
        renderValue: field => renderAttributeValue(field, value => value),
        renderControl: resolved => (
            <EnumSelectControl
                entry={resolved.display as Parameters<typeof EnumSelectControl>[0]['entry']}
                options={ADS_STATE_OPTIONS}
                onValueChange={resolved.onValueChange}
            />
        ),
    }),
    mrmState: defineAttr<MrmStateTO>({
        key: VehicleAttributeKeyTO.MrmState,
        field: 'mrmState',
        label: 'MRM State',
        testId: 'vehicle-attr-mrmState',
        renderValue: field => <MrmStateDisplay mrmField={field} />,
        renderControl: resolved => (
            <MrmStateControl
                entry={resolved.display as Parameters<typeof MrmStateControl>[0]['entry']}
                onValueChange={resolved.onValueChange}
            />
        ),
    }),
    missionState: defineAttr<string>({
        key: VehicleAttributeKeyTO.MissionState,
        field: 'missionState',
        label: 'Mission State',
        testId: 'vehicle-attr-missionState',
        renderValue: field => renderAttributeValue(field, value => value),
        renderControl: resolved => (
            <EnumSelectControl
                entry={resolved.display as Parameters<typeof EnumSelectControl>[0]['entry']}
                options={MISSION_STATE_OPTIONS}
                onValueChange={resolved.onValueChange}
            />
        ),
    }),
    connectivityLatency: defineAttr<number>({
        key: VehicleAttributeKeyTO.ConnectivityLatency,
        field: 'connectivityLatency',
        label: 'Latency',
        testId: 'vehicle-attr-latency',
        renderValue: field => renderAttributeValue(field, value => `${value} ms`),
        renderControl: resolved => (
            <LatencyControl
                entry={resolved.display as Parameters<typeof LatencyControl>[0]['entry']}
                onValueChange={resolved.onValueChange}
            />
        ),
    }),
};

// ── deviation helpers ────────────────────────────────────────────────────────

interface DeviationItemProps {
    deviation: DeviationTO;
}

const DeviationItem = ({ deviation }: DeviationItemProps) => {
    const meta = [
        deviation.id && { label: 'id', value: deviation.id },
        deviation.externalId && { label: 'extId', value: deviation.externalId },
        deviation.origin && { label: 'origin', value: deviation.origin },
        deviation.category && { label: 'category', value: deviation.category },
    ].filter(Boolean) as { label: string; value: string }[];

    return (
        <li
            className={`SimulationVehicleAttributes__deviation SimulationVehicleAttributes__deviation--${deviation.severity.toLowerCase()}`}
        >
            <span className='SimulationVehicleAttributes__deviationSeverity'>{deviation.severity}</span>
            <span className='SimulationVehicleAttributes__deviationName'>{deviation.name}</span>
            {meta.length > 0 && (
                <div className='SimulationVehicleAttributes__deviationMeta'>
                    {meta.map(({ label, value }) => (
                        <span key={label} className='SimulationVehicleAttributes__deviationMetaItem'>
                            <span className='SimulationVehicleAttributes__deviationMetaLabel'>{label}</span>
                            {value}
                        </span>
                    ))}
                </div>
            )}
            {deviation.causes?.length ? (
                <div className='SimulationVehicleAttributes__deviationMeta'>
                    <span className='SimulationVehicleAttributes__deviationMetaLabel'>causes</span>
                    <div className='SimulationVehicleAttributes__deviationCauseTags'>
                        {deviation.causes.map((c, i) => (
                            <span key={`${c}-${i}`} className='SimulationVehicleAttributes__deviationCauseTag'>
                                {c}
                            </span>
                        ))}
                    </div>
                </div>
            ) : null}
        </li>
    );
};

interface DeviationsSectionProps {
    isEditMode: boolean;
    resolved: ResolvedAttribute;
}

const DeviationsSection = ({ isEditMode, resolved }: DeviationsSectionProps) => {
    const deviationList = (resolved.display?.value as DeviationTO[] | undefined) ?? [];

    if (isEditMode) {
        return (
            <div className='SimulationVehicleAttributes__editRow'>
                {resolved.isInError ? (
                    <>
                        <ErrorModeSelect
                            value={resolved.display?.error?.type ?? 'ERROR_UNSPECIFIED'}
                            onChange={resolved.onErrorTypeChange}
                        />
                        <ErrorToggle isActive onClick={resolved.onErrorToggle} />
                    </>
                ) : (
                    <>
                        <div style={{ flex: 1 }}>
                            <DeviationEditor
                                deviations={deviationList}
                                onChange={list => resolved.onValueChange({ value: list })}
                            />
                        </div>
                        <ErrorToggle isActive={false} onClick={resolved.onErrorToggle} />
                    </>
                )}
            </div>
        );
    }

    if (resolved.display?.error) {
        return <AttributeErrorBadge error={resolved.display.error} />;
    }

    if (deviationList.length === 0) {
        return <NoData className='text-14' />;
    }

    return (
        <ul className='SimulationVehicleAttributes__deviations'>
            {deviationList.map(d => (
                <DeviationItem
                    key={d.id ?? d.externalId ?? `${d.name}-${d.origin ?? ''}-${d.category ?? ''}`}
                    deviation={d}
                />
            ))}
        </ul>
    );
};

// ── main component ───────────────────────────────────────────────────────────

interface Props {
    vin: string;
    vehicleRepresentation: VehicleRepresentationTO;
    pinnedAttributes: VehicleAttributeKeyTOType[];
    missionId?: string;
    canEdit: boolean;
    onWriteAttributes: (vin: string, body: LiveControlWriteRequestTO) => Promise<void>;
    onPinAttribute: (vin: string, attribute: VehicleAttributeKeyTOType) => Promise<void>;
    onUnpinAttribute: (vin: string, attribute: VehicleAttributeKeyTOType) => Promise<void>;
}

const SimulationVehicleAttributes = ({
    vin,
    vehicleRepresentation,
    pinnedAttributes,
    missionId,
    canEdit,
    onWriteAttributes,
    onPinAttribute,
    onUnpinAttribute,
}: Props) => {
    const { isEditMode, hasDirtyAttributes, toggleEditMode, handleWriteAttributes, resolveAttribute } =
        useAttributeEditState({ vin, pinnedAttributes, canEdit, onWriteAttributes, onPinAttribute, onUnpinAttribute });

    const renderAttribute = (cfg: AttributeConfig<any>) => {
        const serverField = vehicleRepresentation[cfg.field as keyof VehicleRepresentationTO] as
            | { value?: unknown; error?: AttributeErrorTO }
            | undefined;
        const resolved = resolveAttribute(cfg.field, cfg.key, serverField);

        return (
            <EditableAttribute
                key={cfg.field}
                label={cfg.label}
                data-testid={cfg.testId}
                isEditMode={isEditMode}
                isPinned={resolved.isPinned}
                isInError={resolved.isInError}
                onPinToggle={resolved.onPinToggle}
                errorType={resolved.display?.error?.type}
                onErrorTypeChange={resolved.onErrorTypeChange}
                onErrorToggle={resolved.onErrorToggle}
                editControl={cfg.renderControl(resolved)}
            >
                {cfg.renderValue(serverField as any)}
            </EditableAttribute>
        );
    };

    const { mrmState, deviations } = vehicleRepresentation;

    const mrmStateField = mrmState as { value?: MrmStateTO; error?: AttributeErrorTO } | undefined;
    const mrmStateResolved = resolveAttribute(ATTRIBUTES.mrmState.field, ATTRIBUTES.mrmState.key, mrmStateField);
    const mrmCauses = !mrmStateResolved.isInError
        ? (mrmStateResolved.display?.value as MrmStateTO | undefined)?.causes
        : undefined;

    const deviationsResolved = resolveAttribute(
        'deviations',
        VehicleAttributeKeyTO.Deviations,
        deviations as { value?: unknown; error?: AttributeErrorTO } | undefined
    );

    const editToggle = canEdit ? (
        <button
            type='button'
            className={`SimulationDetailSidebar__editToggle ${isEditMode ? 'SimulationDetailSidebar__editToggle--active' : ''}`}
            onClick={toggleEditMode}
            aria-label={isEditMode ? 'Exit edit mode' : 'Edit vehicle attributes'}
            aria-pressed={isEditMode}
            title={isEditMode ? 'Exit edit mode' : 'Edit vehicle attributes'}
            data-testid='domain-sim-edit-toggle'
        >
            <span className='rioglyph rioglyph-pencil' aria-hidden='true' />
        </button>
    ) : null;

    return (
        <div className='SimulationDetailSidebar__section'>
            <div className='SimulationDetailSidebar__sectionTitle'>
                <span>Vehicle</span>
                {editToggle}
            </div>
            <div className='SimulationDetailSidebar__sectionContent'>
                <AttributeSection title='Motion'>
                    {renderAttribute(ATTRIBUTES.position)}
                    {renderAttribute(ATTRIBUTES.speedMs)}
                    {renderAttribute(ATTRIBUTES.drivingGear)}
                    {renderAttribute(ATTRIBUTES.parkingBrake)}
                </AttributeSection>

                <AttributeSection title='ADS'>
                    {renderAttribute(ATTRIBUTES.adsState)}
                    {renderAttribute(ATTRIBUTES.mrmState)}
                    {!isEditMode && mrmCauses?.length ? (
                        <div className='SimulationVehicleAttributes__mrmCauses'>
                            <span className='SimulationVehicleAttributes__mrmCausesLabel'>Causes</span>
                            <div className='SimulationVehicleAttributes__mrmCauseTags'>
                                {mrmCauses.map((c, i) => (
                                    <span key={`${c}-${i}`} className='SimulationVehicleAttributes__mrmCauseTag'>
                                        {c}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </AttributeSection>

                <AttributeSection title='Mission'>
                    {renderAttribute(ATTRIBUTES.missionState)}
                    <VehicleAttributeRow label='Mission ID'>
                        {missionId ?? <NoData className='text-14' />}
                    </VehicleAttributeRow>
                </AttributeSection>

                <AttributeSection title='Connectivity'>
                    {renderAttribute(ATTRIBUTES.connectivityLatency)}
                </AttributeSection>

                <AttributeSection title='Deviations'>
                    <DeviationsSection isEditMode={isEditMode} resolved={deviationsResolved} />
                </AttributeSection>

                {isEditMode && (
                    <div className='SimulationDetailSidebar__writeButton'>
                        <Button
                            style='primary'
                            onClick={handleWriteAttributes}
                            disabled={!hasDirtyAttributes}
                            data-testid='domain-sim-write-attributes'
                        >
                            <span className='rioglyph rioglyph-send' />
                            Write changes
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SimulationVehicleAttributes;
